import React from "react";
import { Input, InputNumber, Select, Typography } from "@arco-design/web-react";
import DateConditionButton from "./DateConditionPicker";
import PlacePicker from "./PlacePicker";
import { DISTANCE_UNITS, STATUS_VALUES, fieldOf } from "./contactFilter";

const { Text } = Typography;
const toOptions = (pairs) => pairs.map(([value, label]) => ({ value, label }));
const noValueOps = ["empty", "not_empty", "no_list", "is_true", "not_true"];

// The choices for an id rule, by where its ids come from. Signup offers
// campaigns and, under them, their forms (prefixed so both fit one picker).
function idChoices(source, options) {
  switch (source) {
    case "campaigns":
      return [
        ...options.campaigns.map((c) => ({ value: "c:" + c.id, label: c.name })),
        ...options.forms.map((f) => ({ value: "f:" + f.id, label: `${f.campaignName} › ${f.name || "Form"}` })),
      ];
    default:
      return (options[source] || []).map((x) => ({ value: x.id, label: x.name }));
  }
}

// TermEditor edits one rule's condition and values (its field is fixed).
export default function TermEditor({ term, onChange, options }) {
  const info = fieldOf(term, options.customFieldDefs);
  if (!info) return <Text type="secondary">This field no longer exists.</Text>;
  const set = (patch) => onChange({ ...term, ...patch });

  const opPicker = info.ops && (
    <Select
      size="small"
      aria-label="Condition"
      style={{ width: 170 }}
      value={term.op}
      onChange={(op) => set({ op })}
      options={toOptions(info.ops)}
    />
  );
  const needsValue = !noValueOps.includes(term.op);

  switch (info.kind) {
    case "ids": {
      const signup = term.field === "signup";
      const value = signup
        ? [...(term.ids || []).map((id) => "c:" + id), ...(term.form_ids || []).map((id) => "f:" + id)]
        : term.ids || [];
      const pick = (vals) => {
        if (!signup) return set({ ids: vals });
        set({
          ids: vals.filter((v) => v.startsWith("c:")).map((v) => v.slice(2)),
          form_ids: vals.filter((v) => v.startsWith("f:")).map((v) => v.slice(2)),
        });
      };
      return (
        <>
          {opPicker}
          {needsValue && (
            <Select
              size="small"
              mode="multiple"
              aria-label={info.label}
              placeholder={"Choose " + info.label.toLowerCase()}
              style={{ minWidth: 200, flex: 1 }}
              value={value}
              onChange={pick}
              options={idChoices(info.source, options)}
              filterOption={(input, opt) => String(opt.props.children).toLowerCase().includes(input.toLowerCase())}
            />
          )}
          {needsValue && info.statuses && (
            <Select
              size="small"
              aria-label="Membership status"
              style={{ width: 140 }}
              value={term.status || ""}
              onChange={(status) => set({ status: status || undefined })}
              options={toOptions(info.statuses)}
            />
          )}
        </>
      );
    }
    case "status":
      return (
        <>
          {opPicker}
          <Select
            size="small"
            mode="multiple"
            aria-label="Statuses"
            placeholder="Choose statuses"
            style={{ minWidth: 200, flex: 1 }}
            value={term.values || []}
            onChange={(values) => set({ values })}
            options={toOptions(STATUS_VALUES)}
          />
        </>
      );
    case "date":
      return <DateConditionButton value={term.date} onChange={(date) => set({ date })} />;
    case "number":
      return (
        <>
          {opPicker}
          {needsValue && (
            <InputNumber size="small" aria-label="Number" style={{ width: 110 }} value={term.number} onChange={(number) => set({ number })} />
          )}
          {term.op === "between" && (
            <>
              <Text>and</Text>
              <InputNumber size="small" aria-label="Second number" style={{ width: 110 }} value={term.number2} onChange={(number2) => set({ number2 })} />
            </>
          )}
        </>
      );
    case "text":
      return (
        <>
          {opPicker}
          {needsValue && (
            <Input size="small" aria-label="Text" style={{ minWidth: 160, flex: 1 }} value={term.text || ""} onChange={(text) => set({ text })} />
          )}
        </>
      );
    case "bool":
      return opPicker;
    case "distance":
      return (
        <>
          {opPicker}
          <InputNumber size="small" aria-label="Distance" min={1} max={25000} style={{ width: 90 }} value={term.number} onChange={(number) => set({ number })} />
          <Select size="small" aria-label="Unit" style={{ width: 90 }} value={term.unit} onChange={(unit) => set({ unit })} options={toOptions(DISTANCE_UNITS)} />
          <Text>of</Text>
          <PlacePicker value={term.place} onChange={(place) => set({ place })} />
        </>
      );
    case "select":
      return (
        <>
          {opPicker}
          {needsValue && (
            <Select
              size="small"
              mode="multiple"
              aria-label={info.label}
              style={{ minWidth: 180, flex: 1 }}
              value={term.values || []}
              onChange={(values) => set({ values })}
              options={(info.options || []).map((o) => ({ value: o, label: o }))}
            />
          )}
        </>
      );
    default:
      return null;
  }
}
